import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tool, UserData } from '../types';
import { ArrowLeft, Pencil, Loader2, PlayCircle, Info } from 'lucide-react';
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Textarea } from "../components/ui/TextArea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";
import { useToast } from "../components/ui/Toast";
import { TagManagement, TagManagementRef } from "../components/TagManagement";
import { FormField } from '../components/FormField';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { toolFormHelpText, formatHelpText } from '../config/helptext-config';
import { ADMIN_SERVICE_USER, ANNOTATION_URL, DEFAULT_TOOL_TIMEOUT } from '../config/api-config';
import { AccessControlSection } from '../components/AccessControlSection';
import { useAuth } from '../contexts/AuthContext';
import { useUsersApi } from '../apis/users-apis';
import AccessDisplay from '../components/AccessDisplay';
import OwnerSelection from '../components/OwnerSelection';
import { createApiHeadersGetter } from '../utils/api-headers';
import { Checkbox } from '../components/ui/Checkbox';

interface AccessControl {
  editAccessType: string;
  editAllowedUsers: string[];
  viewAccessType: string;
  viewAllowedUsers: string[];
}

// View Mode Components
const ViewModeField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="mb-6">
    <h3 className="text-sm font-medium text-gray-500 mb-2">{label}</h3>
    {typeof value === 'string' ? (
      <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap border border-gray-200">
        {value || 'No content provided'}
      </div>
    ) : (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        {value}
      </div>
    )}
  </div>
);

interface Parameter {
  key: string;
  description: string;
  type: string;
  options?: string;
  example?: string;
}

const ViewModeParameters: React.FC<{ parameters: Parameter[] }> = ({ parameters = [] }) => {
  if (!Array.isArray(parameters) || parameters.length === 0) {
    return <div className="text-gray-500 italic">No parameters configured</div>;
  }

  return (
    <div className="space-y-4">
      {parameters.map((param, index) => (
        <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-start">
            <div className="flex-grow">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">{param.key}</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                  {param.type}
                </span>
              </div>
              <p className="text-gray-600 mt-1">{param.description}</p>
              {(param.options || param.example) && (
                <div className="mt-2 space-y-1 text-sm">
                  {param.options && (
                    <div className="text-gray-600">
                      <span className="font-medium">Options:</span> {param.options}
                    </div>
                  )}
                  {param.example && (
                    <div className="text-gray-600">
                      <span className="font-medium">Example:</span> {param.example}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const ViewModeOutput: React.FC<{ output: any }> = ({ output }) => {
  if (!output || !output.parameters || !output.parameters.output) {
    return <div className="text-gray-500 italic">No output configured</div>;
  }

  const outputData = output.parameters.output;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-start">
        <div className="flex-grow">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">output</span>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
              {outputData.type}
            </span>
          </div>
          <p className="text-gray-600 mt-1">{outputData.description}</p>
        </div>
      </div>
    </div>
  );
};

const ViewModeAnnotation: React.FC<{ annotation?: string }> = ({ annotation }) => {
  if (!annotation) {
    return <div className="text-gray-500 italic">No annotation available</div>;
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap">
      {annotation}
    </div>
  );
};
interface ToolFormProps {
  tool: Tool;
  originalTool?: Tool | null;
  onToolChange: (tool: Tool) => void;
  onSubmit: () => Promise<void>;
  onCancel: () => void;
  isEditing?: boolean;
  readOnly?: boolean;
  onEditClick?: () => void;
  onDiscardChanges?: () => void;
  hasUnsavedChanges?: boolean;
  setIsReadOnly: (value: boolean) => void;
  tagSuggestions?: string[];
  existingToolNames?: string[];
  currentUserId: string;
  onOwnerChange?: (newOwnerId: string) => Promise<void>;
  isSaving?: boolean;
}

export interface ToolFormRef {
  validateForm: () => Promise<boolean>;
  getErrors: () => Record<string, string>;
}

const LoadingOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      <span className="text-lg font-medium">Generating Annotation... Please wait</span>
    </div>
  </div>
);

const SaveOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      <span className="text-lg font-medium">Saving Tool... Please wait</span>
    </div>
  </div>
);

interface UserAPIResponse {
  users: {
    userId: string;
    email: string;
    isAdmin: boolean;
    isActive: boolean;
  }[];
}

  const ToolForm = forwardRef<ToolFormRef, ToolFormProps>((props, ref) => {
    const {
      tool,
      onToolChange,
      onSubmit,
      onCancel,
      isEditing = false,
      readOnly = false,
      onEditClick,
      onDiscardChanges,
      tagSuggestions = []
    } = props;

    const { user } = useAuth();
    const navigate = useNavigate();

  const { addToast } = useToast();
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isAuthFieldTouched, setIsAuthFieldTouched] = useState(false);
  const [isAuthEnabled, setIsAuthEnabled] = useState(tool.isAuthPresent || false);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [parameterToDelete, setParameterToDelete] = useState<number | null>(null);
  const [isAddingParameter, setIsAddingParameter] = useState(false);
  const tagManagementRef = useRef<TagManagementRef>(null);
  const [isCoreTool, setIsCoreTool] = useState(tool.isCore || false);
  const [adminServiceUserId, setAdminServiceUserId] = useState<string>('');

  // Parameter Management State
  const [newParamKey, setNewParamKey] = useState("");
  const [newParamDesc, setNewParamDesc] = useState("");
  const [newParamType, setNewParamType] = useState("str");
  const [newParamOptions, setNewParamOptions] = useState("");
  const [newParamExample, setNewParamExample] = useState("");

  const [editingParam, setEditingParam] = useState<number | null>(null);
  const [editParamKey, setEditParamKey] = useState("");
  const [editParamDesc, setEditParamDesc] = useState("");
  const [editParamType, setEditParamType] = useState("str");
  const [editParamOptions, setEditParamOptions] = useState("");
  const [editParamExample, setEditParamExample] = useState("");
  const [editErrors, setEditErrors] = useState<{
    key?: string;
    description?: string;
  }>({});

  const [isAnnotationEditing, setIsAnnotationEditing] = useState(false);
  const [tempAnnotation, setTempAnnotation] = useState('');
  const [isGeneratingAnnotation, setIsGeneratingAnnotation] = useState(false);
  const { getUsers } = useUsersApi();
  const { getAuthToken } = useAuth();
  const getHeaders = createApiHeadersGetter(getAuthToken);

  useEffect(() => {
    const fetchAdminStatus = async () => {
      if (user?.sub) {
        try {
          const usersData = await getUsers();
          const userData = usersData.find((u) => u.userId === user.sub);
          setIsCurrentUserAdmin(userData?.isAdmin || false);
          
          // Find the admin service account ID
          const adminServiceAccount = usersData.find(u => 
            u.email === ADMIN_SERVICE_USER
          );
          
          if (adminServiceAccount) {
            setAdminServiceUserId(adminServiceAccount.userId);
          } else {
            console.warn(`Admin service account ${ADMIN_SERVICE_USER} not found in users list`);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setIsCurrentUserAdmin(false);
        }
      }
    };
  
    fetchAdminStatus();
  }, [user?.sub]);

  useEffect(() => {
    // When isCoreTool is enabled, set default access control to "Owner Only"
    // and set owner to admin service account
    if (isCoreTool) {
      onToolChange({
        ...tool,
        isCore: true,
        ownerId: adminServiceUserId || tool.ownerId,
        accessControl: {
          viewAccessType: 'Owner Only',
          viewAllowedUsers: [],
          editAccessType: 'Owner Only',
          editAllowedUsers: []
        }
      });
    } else {
      onToolChange({
        ...tool,
        isCore: false
      });
    }
  }, [isCoreTool, adminServiceUserId]);

  const validateToolName = (name: string, existingNames: string[], currentToolId?: string): { isValid: boolean; error: string } => {
    if (!name.trim()) {
      return { isValid: false, error: 'Tool Name is required' };
    }

    if (name.length > 63) {
      return { isValid: false, error: 'Tool Name must be less than 64 characters' };
    }
    
    // Check for disallowed characters (hyphens and spaces)
    if (name.includes('-') || name.includes(' ')) {
      return { 
        isValid: false, 
        error: 'Tool Name can only contain letters, numbers, and underscores. Hyphens and spaces are not allowed'
      };
    }

    // Check for uppercase letters
    if (/[A-Z]/.test(name)) {
      return {
        isValid: false,
        error: 'Tool Name must be in lowercase'
      };
    }
  
    // Only allow letters, numbers, and underscores
    const validNameRegex = /^[a-z0-9_]+$/;
    if (!validNameRegex.test(name)) {
      return { 
        isValid: false, 
        error: 'Tool Name can only contain lower case letters, numbers, and underscores'
      };
    }

    // Check for uniqueness - only if we have existing names
    if (existingNames.length > 0) {
      const isDuplicate = existingNames.some(existingName => 
        existingName.toLowerCase() === name.toLowerCase() && 
        (!currentToolId || currentToolId !== existingName) // Exclude current tool when editing
      );
      
      if (isDuplicate) {
        return {
          isValid: false,
          error: 'A Tool with this name already exists. Please choose a different name.'
        };
      }
    }
  
    return { isValid: true, error: '' }; // Always include error property
  };

  const validateForm = async (): Promise<boolean> => {
    if (readOnly) return true;
    
    const newErrors: Record<string, string> = {};
  
    // Ensure tool object exists
    if (!tool) {
      return false;
    }
  
    // Validate tool name
    if (!tool.toolName) {
      newErrors.toolName = 'Tool Name is required';
    } else {
      const toolNameValidation = validateToolName(tool.toolName, props.existingToolNames || [], tool.toolId);
      if (!toolNameValidation.isValid) {
        newErrors.toolName = toolNameValidation.error;
      }
    }
  
    // Validate required fields with null checks
    if (!tool.toolDescription?.trim()) {
      newErrors.toolDescription = 'Tool Description is required';
    }
    
    if (!tool.toolUrlEndpoint?.trim()) {
      newErrors.toolUrlEndpoint = 'Tool URL Endpoint is required';
    }
    
    // Validate authentication if enabled AND either:
    // 1. The field has been touched (new input submitted)
    // 2. The checkbox was toggled (isAuthFieldTouched will be true)
    if (isAuthEnabled && (!tool.toolAuthentication || !tool.toolAuthentication.trim()) && isAuthFieldTouched) {
      newErrors.toolAuthentication = 'Tool Authentication is required when authentication is enabled';
    }
  
    // Validate timeout if provided
    if (!tool.toolTimeout) {
      newErrors.toolTimeout = 'Timeout is required';
    } else {
      const timeout = Number(tool.toolTimeout);
      if (isNaN(timeout) || timeout <= 0 || !Number.isInteger(timeout)) {
        newErrors.toolTimeout = 'Timeout must be a positive whole number';
      }
    }
  
    // Ensure toolParameters exists
    if (!tool.toolParameters) {
      tool.toolParameters = {
        parameters: [],
        testMode: true
      };
    }
  
    // Initialize parameters array if it doesn't exist
    if (!tool.toolParameters.parameters) {
      tool.toolParameters.parameters = [];
    }
  
    // Validate output parameters - Enhanced validation for output field
    if (!tool.toolOutput?.parameters?.output) {
      newErrors.output = 'Output is required';
    } else if (!tool.toolOutput.parameters.output.description?.trim()) {
      newErrors.output = 'Output description is required';
    } else if (!tool.toolOutput.parameters.output.type) {
      newErrors.output = 'Output type is required';
    }

    // Annotation is required for all tools
    if (!tool.toolAnnotation?.trim()) {
      newErrors.toolAnnotation = 'Annotation is required';
    }

    if (!isCoreTool) {
      if (!tool.accessControl.viewAccessType) {
        newErrors.viewAccessType = 'View Access Type is required';
      }
    
      if (!tool.accessControl.editAccessType) {
        newErrors.editAccessType = 'Edit Access Type is required';
      }
    }
  
    setErrors(newErrors);

    // If validation passed, process any pending tags
    if (Object.keys(newErrors).length === 0) {
      // Process pending tags if any
      if (tagManagementRef.current) {
        await tagManagementRef.current.processPendingTags();
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleCoreToolChange = (checked: boolean) => {
    if (readOnly) return;
    setIsCoreTool(checked);
  };

  useImperativeHandle(ref, () => ({
    validateForm,
    getErrors: () => errors
  }));

  const handleInputChange = (field: keyof Tool, value: string | string[]) => {
    if (readOnly) return;
  
    if (field === 'toolAuthentication' && !isAuthFieldTouched) {
      setIsAuthFieldTouched(true);
      value = ''; // Clear the field when user starts editing
    }
  
    onToolChange({
      ...tool,
      [field]: value
    });
  
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleInputTouch = (fieldName: string) => {
    setTouchedFields(prev => new Set(prev).add(fieldName));
  };

  const handleParameterAdd = () => {
    if (newParamKey && newParamDesc) {
      onToolChange({
        ...tool,
        toolParameters: {
          ...tool.toolParameters,
          parameters: [
            ...tool.toolParameters.parameters,
            {
              key: newParamKey,
              description: newParamDesc,
              type: newParamType,
              ...(newParamOptions && { options: newParamOptions }),
              ...(newParamExample && { example: newParamExample })
            }
          ]
        }
      });
      setNewParamKey("");
      setNewParamDesc("");
      setNewParamOptions("");
      setNewParamExample("");
      setNewParamType("str");
      setTouchedFields(new Set());
    }
  };

  const handleParameterEdit = (index: number) => {
    const validation = validateParameter(editParamKey, editParamDesc);
    
    if (!validation.isValid) {
      setEditErrors(validation.errors);
      return;
    }

    onToolChange({
      ...tool,
      toolParameters: {
        ...tool.toolParameters,
        parameters: tool.toolParameters.parameters.map((param, i) => 
          i === index ? {
            key: editParamKey,
            description: editParamDesc,
            type: editParamType,
            ...(editParamOptions && { options: editParamOptions }),
            ...(editParamExample && { example: editParamExample })
          } : param
        )
      }
    });
    setEditingParam(null);
    setEditErrors({});
  };

  const handleParameterRemove = (index: number) => {
    setParameterToDelete(index);
  };
  
  const confirmParameterRemove = () => {
    if (parameterToDelete !== null) {
      onToolChange({
        ...tool,
        toolParameters: {
          ...tool.toolParameters,
          parameters: tool.toolParameters.parameters.filter((_, i) => i !== parameterToDelete)
        }
      });
      setParameterToDelete(null);
    }
  };

  const validateParameter = (key: string, description: string) => {
    const errors: { key?: string; description?: string } = {};
    
    if (!key.trim()) {
      errors.key = 'Parameter key is required';
    }
    if (!description.trim()) {
      errors.description = 'Description is required';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  const hasEditAccess = (accessControl: AccessControl) => {
    const currentUserId = user?.sub;
    const isOwner = tool.ownerId === currentUserId;
    
    if (!currentUserId) return false;
    
    // Admins always have edit access
    if (isCurrentUserAdmin) return true;
    
    // Owners always have edit access to their own tools
    if (isOwner) return true;
    
    // For non-admin, non-owner users, check access control settings
    const { editAccessType, editAllowedUsers } = accessControl;
    
    switch (editAccessType) {
      case 'All Users':
        return true;
      case 'Allowed Users':
        return editAllowedUsers.includes(currentUserId);
      case 'Owner Only':
        return false; // Already checked for owner above
      default:
        return false;
    }
  };

  const AccessControlViewSection = () => {
    const [ownerDetails, setOwnerDetails] = useState({ email: '', name: '' });
    
    useEffect(() => {
      const fetchOwnerDetails = async () => {
        try {
          const usersData = await getUsers();
          const owner = usersData.find((user) => user.userId === tool.ownerId);
          if (owner) {
            setOwnerDetails({
              email: owner.email,
              name: owner.email //TODO: Add name to user table
            });
          }
        } catch (error) {
          console.error('Error fetching owner details:', error);
        }
      };
      
      fetchOwnerDetails();
    }, [tool.ownerId]);

    // For Core Tools, show a special message
    if (tool.isCore) {
      return (
        <ViewModeField
          label="Access Control"
          value={
            <div className="flex items-center space-x-2 text-gray-700">
              <Info className="h-5 w-5 text-blue-500" />
              <div>
                <p className="font-medium">Administrator Only</p>
                <p className="text-sm text-gray-600">
                  This Core Tool is managed by administrators only.
                </p>
              </div>
            </div>
          }
        />
      );
    }
    
  
    return (
      <ViewModeField
        label="Access Control"
        value={
      <AccessDisplay
        viewAccessType={tool.accessControl.viewAccessType}
        editAccessType={tool.accessControl.editAccessType}
        viewAllowedUsers={tool.accessControl.viewAccessType === 'Allowed Users' 
          ? tool.accessControl.viewAllowedUsers 
          : []}
        editAllowedUsers={tool.accessControl.editAccessType === 'Allowed Users' 
          ? tool.accessControl.editAllowedUsers 
          : []}
        ownerEmail={ownerDetails.email}
        ownerName={ownerDetails.name}
      />
        }
      />
    );
  };

  const renderViewMode = () => (
    <Card className="max-w-3xl mx-auto">
      <CardHeader className="flex flex-col gap-4 pb-2">
  <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full">
    <CardTitle className="text-2xl break-words max-w-full overflow-hidden text-ellipsis">
      {tool.toolName}
    </CardTitle>
    <div className="flex gap-2 mt-2 sm:mt-0 flex-shrink-0">
      <Button
        variant="outline"
        onClick={() => navigate(`/studio/tools/test/${tool.toolId}`)}
        className="border-green-500 text-green-500 hover:bg-green-50 hover:text-green-600"
      >
        <PlayCircle className="mr-2 h-4 w-4" />
        Test
      </Button>
      {hasEditAccess(tool.accessControl) && (
        <Button
          variant="outline"
          onClick={onEditClick}
          className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
        >
          <Pencil className="mr-2 h-4 w-4" />
          Edit
        </Button>
      )}
      <Button
        variant="outline"
        onClick={onCancel}
        className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Tools
      </Button>
    </div>
  </div>
</CardHeader>

      <CardContent className="space-y-6">
  <ViewModeField 
    label="Description" 
    value={tool.toolDescription} 
  />
  
  <ViewModeField 
    label="URL Endpoint" 
    value={tool.toolUrlEndpoint} 
  />
  
  <ViewModeField 
    label="Authentication" 
    value={tool.isAuthPresent ? "••••••••" : "No authentication"} 
  />
  
  <ViewModeField 
    label="Timeout (seconds)" 
    value={tool.toolTimeout || DEFAULT_TOOL_TIMEOUT} 
  />
  
  <ViewModeField 
    label="Tags" 
    value={
      <TagManagement
        tags={tool.toolTags || []}
        onChange={() => {}}
        readOnly={true}
        suggestions={tagSuggestions}
      />
    }
  />

  <ViewModeField
    label="Input Parameters"
    value={
      <Card className="bg-gray-50 border-none shadow-none p-4">
        <ViewModeParameters parameters={tool.toolParameters?.parameters ?? []}  />
      </Card>
    }
  />

  <ViewModeField
    label="Output"
    value={
      <Card className="bg-gray-50 border-none shadow-none p-4">
        <ViewModeOutput output={tool.toolOutput} />
      </Card>
    }
  />

  <ViewModeField
    label="Annotation"
    value={
      <Card className="bg-gray-50 border-none shadow-none p-4">
        <ViewModeAnnotation annotation={tool.toolAnnotation} />
      </Card>
    }
  />

<AccessControlViewSection />

</CardContent>
    </Card>
  );

  const renderAuthenticationField = () => (
    <FormField
      helpTitle="Tool Authentication Help"
      label={
        <div className="flex items-center justify-between w-full">
          <div className="flex-grow">Tool Authentication {isAuthEnabled && <span className="text-red-500">*</span>}</div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <Checkbox 
              checked={isAuthEnabled}
              onCheckedChange={(checked: boolean) => {
                setIsAuthEnabled(checked);
                if (!checked) {
                  handleInputChange('toolAuthentication', '');
                  setIsAuthFieldTouched(false);
                } else {
                  // When enabling auth, mark the field as touched to trigger validation
                  setIsAuthFieldTouched(true);
                }
              }}
            />
          </div>
        </div>
      }
      helpText={formatHelpText(toolFormHelpText.toolAuthentication)}
      error={errors.toolAuthentication}
    >
      {!isAuthEnabled ? (
        <p className="text-sm text-gray-500 mt-1">
          {/* Authentication is disabled for this tool */}
        </p>
      ) : (
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="text"
                value={isAuthFieldTouched ? tool.toolAuthentication : '••••••••'}
                onChange={(e) => handleInputChange('toolAuthentication', e.target.value)}
                placeholder="Enter new authentication"
                className={errors.toolAuthentication ? 'border-red-500' : ''}
                onFocus={() => {
                  if (!isAuthFieldTouched) {
                    handleInputChange('toolAuthentication', '');
                  }
                }}
                required={isAuthEnabled}
              />
            </div>
          </div>
          {(!tool.toolUrlEndpoint) && (
            <p className="text-sm text-gray-500 mt-1">
              {/* Please enter a URL endpoint before testing authentication */}
            </p>
          )}
        </div>
      )}
    </FormField>
  );
  

  const renderTagManagementField = () => (
    <FormField
      label="Tool Tags"
      helpText={formatHelpText(toolFormHelpText.toolTags)}
    >
      <TagManagement
        ref={tagManagementRef}
        tags={tool.toolTags || []}
        onChange={(newTags) => {
          onToolChange({
            ...tool,
            toolTags: newTags
          });
        }}
        readOnly={false}
        suggestions={tagSuggestions}
        className="mt-1"
      />
      <p className="text-sm text-muted-foreground mt-1">
        {/* Search existing tags or type to add new ones */}
      </p>
    </FormField>
  );

const renderEditMode = () => (
  <Card className="max-w-3xl mx-auto">
    <CardHeader className="flex flex-col gap-4">
  <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full">
    <CardTitle className="text-2xl">
      {isEditing ? 'Edit Tool' : 'Add New Tool'}
    </CardTitle>
    <Button
      variant="outline"
      onClick={onCancel}
      className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600 mt-2 sm:mt-0"
    >
      <ArrowLeft className="mr-2 h-4 w-4" />
      Back to Tools
    </Button>
  </div>
</CardHeader>

    <CardContent className="space-y-6">
      <FormField
        label="Tool Name"
        required
        helpText={formatHelpText(toolFormHelpText.toolName)}
        error={errors.toolName}
      >
        <Input
    value={tool.toolName}
    onChange={(e) => {
      const newValue = e.target.value;
      handleInputChange('toolName', newValue);
      
      // Validate on change for immediate feedback
      const validation = validateToolName(newValue, props.existingToolNames || [], tool.toolId);
      if (!validation.isValid) {
        setErrors(prev => ({
          ...prev,
          toolName: validation.error
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.toolName;
          return newErrors;
        });
      }
    }}
    placeholder="Enter tool name"
    className={errors.toolName ? 'border-red-500' : ''}
  />
      </FormField>

      <FormField
        label="Tool Description"
        required
        helpText={formatHelpText(toolFormHelpText.toolDescription)}
        error={errors.toolDescription}
      >
        <Textarea
          value={tool.toolDescription}
          onChange={(e) => handleInputChange('toolDescription', e.target.value)}
          placeholder="Enter tool description"
          rows={3}
          className={errors.toolDescription ? 'border-red-500' : ''}
        />
      </FormField>

      {renderTagManagementField()}

      <FormField
        label="URL Endpoint"
        required
        helpText={formatHelpText(toolFormHelpText.toolUrlEndpoint)}
        error={errors.toolUrlEndpoint}
      >
        <Input
          value={tool.toolUrlEndpoint}
          onChange={(e) => handleInputChange('toolUrlEndpoint', e.target.value)}
          placeholder="https://api.example.com/tool"
          className={errors.toolUrlEndpoint ? 'border-red-500' : ''}
        />
      </FormField>

      {renderAuthenticationField()}

      <FormField
  label="Tool Timeout"
  required
  helpText={formatHelpText(toolFormHelpText.toolTimeout)}
  error={errors.toolTimeout}
>
  <Input
    type="number"
    min="1"
    step="1"
    value={tool.toolTimeout || DEFAULT_TOOL_TIMEOUT}
    onChange={(e) => {
      const value = e.target.value;
      // Only allow positive integers but keep as string
      if (!value || (parseInt(value) > 0 && Number.isInteger(parseFloat(value)))) {
        handleInputChange('toolTimeout', value); // This will be saved as string
      }
    }}
    placeholder="Enter timeout in seconds"
    className={errors.toolTimeout ? 'border-red-500' : ''}
  />
</FormField>

      <FormField
  label="Input Parameters"
  helpText={formatHelpText(toolFormHelpText.toolInputParameters)}
>

{!isAddingParameter ? (
    <div className="flex justify-start">
      <Button
        type="button"
        onClick={() => setIsAddingParameter(true)}
        className="bg-blue-500 hover:bg-blue-600 text-white"
      >
        Add Input Parameter
      </Button>
    </div>
  ) : (
    <div className="space-y-4">
      <div className="flex space-x-2">
        <div className="flex-1">
          <div className="mb-1 text-sm font-medium text-gray-700">
            Parameter Key <span className="text-red-500">*</span>
          </div>
          <Input
            value={newParamKey}
            onChange={(e) => setNewParamKey(e.target.value)}
            onBlur={() => handleInputTouch('paramKey')}
            placeholder="Enter parameter key"
            className={`${touchedFields.has('paramKey') && !newParamKey ? 'border-red-300' : ''}`}
            required
          />
          {touchedFields.has('paramKey') && !newParamKey && 
            <p className="mt-1 text-sm text-red-500">Parameter key is required</p>
          }
        </div>
        
        <div className="flex-1">
          <div className="mb-1 text-sm font-medium text-gray-700">
            Description <span className="text-red-500">*</span>
          </div>
          <Input
            value={newParamDesc}
            onChange={(e) => setNewParamDesc(e.target.value)}
            onBlur={() => handleInputTouch('paramDesc')}
            placeholder="Enter description"
            className={`${touchedFields.has('paramDesc') && !newParamDesc ? 'border-red-300' : ''}`}
            required
          />
          {touchedFields.has('paramDesc') && !newParamDesc && 
            <p className="mt-1 text-sm text-red-500">Description is required</p>
          }
        </div>

        <div>
          <div className="mb-1 text-sm font-medium text-gray-700">
            Type <span className="text-red-500">*</span>
          </div>
          <Select value={newParamType} onValueChange={setNewParamType}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="int">Integer</SelectItem>
              <SelectItem value="float">Float</SelectItem>
              <SelectItem value="complex">Complex</SelectItem>
              <SelectItem value="bool">Boolean</SelectItem>
              <SelectItem value="str">String</SelectItem>
              <SelectItem value="list">List</SelectItem>
              <SelectItem value="tuple">Tuple</SelectItem>
              <SelectItem value="dict">Dictionary</SelectItem>
              <SelectItem value="set">Set</SelectItem>
              <SelectItem value="frozenset">Frozen Set</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex space-x-2">
        <div className="flex-1">
          <div className="mb-1 text-sm font-medium text-gray-700 flex items-center">
            Parameter Options
            <span className="ml-1 text-gray-500 text-sm">(Optional)</span>
          </div>
          <Input
            value={newParamOptions}
            onChange={(e) => setNewParamOptions(e.target.value)}
            placeholder="Add parameter options if any"
          />
        </div>
        
        <div className="flex-1">
          <div className="mb-1 text-sm font-medium text-gray-700 flex items-center">
            Parameter Example
            <span className="ml-1 text-gray-500 text-sm">(Optional)</span>
          </div>
          <Input
            value={newParamExample}
            onChange={(e) => setNewParamExample(e.target.value)}
            placeholder="Add usage example if any"
          />
        </div>

        <div className="flex items-end space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setIsAddingParameter(false);
              setNewParamKey("");
              setNewParamDesc("");
              setNewParamType("str");
              setNewParamOptions("");
              setNewParamExample("");
              setTouchedFields(new Set());
            }}
            className="text-gray-600 hover:text-gray-800"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              handleParameterAdd();
              setIsAddingParameter(false);
            }}
            className="bg-blue-500 hover:bg-blue-600 text-white"
            disabled={!newParamKey || !newParamDesc}
          >
            Add
          </Button>
        </div>
      </div>
    </div>
  )}
</FormField>

  {/* Parameters List */}
  <div className="space-y-2 mt-4">
  {tool.toolParameters.parameters.map((param, index) => (
    <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex justify-between items-start">
        <div className="flex-grow">
          <span className="font-medium">{param.key}</span>
          {editingParam === index ? (
            <div className="space-y-2 mt-2">
              <div className="flex items-center space-x-2">
                <div className="flex-1">
                  <Input
                    value={editParamKey}
                    onChange={(e) => {
                      setEditParamKey(e.target.value);
                      if (editErrors.key) {
                        setEditErrors(prev => ({ ...prev, key: undefined }));
                      }
                    }}
                    placeholder="Parameter Key"
                    className={`${editErrors.key ? 'border-red-300' : ''}`}
                  />
                  {editErrors.key && (
                    <p className="mt-1 text-sm text-red-500">{editErrors.key}</p>
                  )}
                </div>
                
                <div className="flex-1">
                  <Input
                    value={editParamDesc}
                    onChange={(e) => {
                      setEditParamDesc(e.target.value);
                      if (editErrors.description) {
                        setEditErrors(prev => ({ ...prev, description: undefined }));
                      }
                    }}
                    placeholder="Description"
                    className={`${editErrors.description ? 'border-red-300' : ''}`}
                  />
                  {editErrors.description && (
                    <p className="mt-1 text-sm text-red-500">{editErrors.description}</p>
                  )}
                </div>

                <Select value={editParamType} onValueChange={setEditParamType}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="int">Integer</SelectItem>
                    <SelectItem value="float">Float</SelectItem>
                    <SelectItem value="complex">Complex</SelectItem>
                    <SelectItem value="bool">Boolean</SelectItem>
                    <SelectItem value="str">String</SelectItem>
                    <SelectItem value="list">List</SelectItem>
                    <SelectItem value="tuple">Tuple</SelectItem>
                    <SelectItem value="dict">Dictionary</SelectItem>
                    <SelectItem value="set">Set</SelectItem>
                    <SelectItem value="frozenset">Frozen Set</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex space-x-2">
                <Input
                  value={editParamOptions}
                  onChange={(e) => setEditParamOptions(e.target.value)}
                  placeholder="Parameter Options (Optional)"
                  className="flex-1"
                />
                <Input
                  value={editParamExample}
                  onChange={(e) => setEditParamExample(e.target.value)}
                  placeholder="Parameter Example (Optional)"
                  className="flex-1"
                />
              </div>

              <div className="flex justify-end space-x-2 mt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingParam(null);
                    setEditErrors({});
                  }}
                  className="text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleParameterEdit(index)}
                  className="text-green-600 hover:text-green-800"
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <>
              <span className="ml-2 text-gray-600">- {param.description}</span>
              <span className="ml-2 text-gray-500">({param.type})</span>
              {(param.options || param.example) && (
                <div className="text-sm text-gray-500 mt-1">
                  {param.options && <span>Options: {param.options}</span>}
                  {param.options && param.example && <span> • </span>}
                  {param.example && <span>Example: {param.example}</span>}
                </div>
              )}
            </>
          )}
        </div>
        {editingParam !== index && ( // Only show Edit/Remove when this parameter is not being edited
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setEditingParam(index);
                setEditParamKey(param.key);
                setEditParamDesc(param.description);
                setEditParamType(param.type);
                setEditParamOptions(param.options || "");
                setEditParamExample(param.example || "");
              }}
              className="text-blue-600 hover:text-blue-800"
            >
              Edit
            </Button>
            <Button
              variant="outline"
              onClick={() => handleParameterRemove(index)}
              className="text-red-600 hover:text-red-800"
            >
              Remove
            </Button>
          </div>
        )}
      </div>
    </div>
  ))}
</div>

<FormField
  label="Output"
  helpText={formatHelpText(toolFormHelpText.toolOutput)}
  required
  error={errors.output}
>
  <div className="space-y-4">
    <div className="flex space-x-2">
      <div className="flex-1">
        <Input
          value={tool.toolOutput.parameters.output?.description || ''}
          onChange={(e) => {
            onToolChange({
              ...tool,
              toolOutput: {
                ...tool.toolOutput,
                parameters: {
                  output: {
                    description: e.target.value,
                    type: tool.toolOutput.parameters.output?.type || 'str'
                  }
                }
              }
            });
            
            // Clear error when user edits the field
            if (errors.output) {
              setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.output;
                return newErrors;
              });
            }
          }}
          placeholder="Description"
          className={`w-full ${errors.output ? 'border-red-500' : ''}`}
        />
      </div>
      <Select 
        value={tool.toolOutput.parameters.output?.type || 'str'}
        onValueChange={(value) => {
          onToolChange({
            ...tool,
            toolOutput: {
              ...tool.toolOutput,
              parameters: {
                output: {
                  description: tool.toolOutput.parameters.output?.description || '',
                  type: value
                }
              }
            }
          });
          
          // Clear error when user edits the field
          if (errors.output) {
            setErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.output;
              return newErrors;
            });
          }
        }}
      >
        <SelectTrigger className={`w-32 ${errors.output ? 'border-red-500' : ''}`}>
          <SelectValue placeholder="Type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="int">Integer</SelectItem>
          <SelectItem value="float">Float</SelectItem>
          <SelectItem value="complex">Complex</SelectItem>
          <SelectItem value="bool">Boolean</SelectItem>
          <SelectItem value="str">String</SelectItem>
          <SelectItem value="list">List</SelectItem>
          <SelectItem value="tuple">Tuple</SelectItem>
          <SelectItem value="dict">Dictionary</SelectItem>
          <SelectItem value="set">Set</SelectItem>
          <SelectItem value="frozenset">Frozen Set</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
</FormField>

{/* Generate Annotation Section */}
<FormField
  label="Annotation"
  helpText={formatHelpText(toolFormHelpText.toolAnnotation)}
  required
  error={errors.toolAnnotation}
>
  <div className="space-y-4">
    <div className="flex justify-between items-center">
      <Button
        type="button"
        onClick={async () => {
          setIsGeneratingAnnotation(true);
          try {
            const headers = await getHeaders();
            console.log("Tool output:", tool.toolOutput);
            const annotationAPIInputs = {
              name: tool.toolName,
              description: tool.toolDescription,
              inputParameters: tool.toolParameters.parameters,
              outputResults: tool.toolOutput
            };
            console.log('Generating annotation for tool:', JSON.stringify(annotationAPIInputs));
            
            const response = await fetch(ANNOTATION_URL, {
              method: 'POST',
              headers,
              body: JSON.stringify(annotationAPIInputs)
            });
            
            if (!response.ok) {
              throw new Error('Failed to generate annotation');
            }
            
            const { aiAnnotation } = await response.json();
            console.log('Generated annotation:\n', aiAnnotation);
            onToolChange({
              ...tool,
              toolAnnotation: aiAnnotation
            });
            setTempAnnotation(aiAnnotation);
            
            // Clear error when annotation is generated
            if (errors.toolAnnotation) {
              setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.toolAnnotation;
                return newErrors;
              });
            }
            
            addToast({
              title: "Success",
              description: "Annotation generated successfully",
              variant: "default"
            });
          } catch (error) {
            addToast({
              title: "Error",
              description: "Failed to generate annotation",
              variant: "destructive"
            });
          } finally {
            setIsGeneratingAnnotation(false);
          }
        }}
        className="bg-blue-500 hover:bg-blue-600 text-white"
        disabled={isGeneratingAnnotation}
      >
        {isGeneratingAnnotation ? (
          <div className="flex items-center space-x-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Generating...</span>
          </div>
        ) : (
          'Generate Annotation'
        )}
      </Button>
      {tool.toolAnnotation && !isAnnotationEditing && (
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setIsAnnotationEditing(true);
            setTempAnnotation(tool.toolAnnotation || '');
          }}
          className="text-blue-600 hover:text-blue-800"
        >
          Edit
        </Button>
      )}
      {isAnnotationEditing && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onToolChange({
                ...tool,
                toolAnnotation: tempAnnotation
              });
              
              // Clear error when annotation is saved
              if (errors.toolAnnotation && tempAnnotation.trim()) {
                setErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors.toolAnnotation;
                  return newErrors;
                });
              }
              
              setIsAnnotationEditing(false);
            }}
            className="text-green-600 hover:text-green-800"
          >
            Save
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setTempAnnotation(tool.toolAnnotation || '');
              setIsAnnotationEditing(false);
            }}
            className="text-gray-600 hover:text-gray-800"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
    {(tool.toolAnnotation || errors.toolAnnotation) && (
      <Textarea
        value={isAnnotationEditing ? tempAnnotation : tool.toolAnnotation}
        onChange={(e) => {
          if (isAnnotationEditing) {
            setTempAnnotation(e.target.value);
          }
        }}
        readOnly={!isAnnotationEditing}
        rows={6}
        className={`font-mono ${!isAnnotationEditing ? 'bg-gray-50' : ''} ${
          errors.toolAnnotation ? 'border-red-500' : ''
        }`}
      />
    )}
  </div>
</FormField>

{isCurrentUserAdmin && (
  <div className="flex items-start space-x-2 border p-4 rounded-md bg-gray-50">
    <Checkbox 
      id="core-tool" 
      checked={isCoreTool}
      onCheckedChange={handleCoreToolChange}
    />
    <div className="flex flex-col">
      <label 
        htmlFor="core-tool" 
        className="text-sm font-medium cursor-pointer"
      >
        Core Tool (Admin Only)
      </label>
      <p className="text-xs text-gray-500 mt-1">
        Core tools have fixed access control set to "Owner Only", will be owned by the admin service account, and cannot be modified by regular users.
      </p>
    </div>
  </div>
)}

{!isCoreTool && (
  <AccessControlSection<Tool>
    entity={tool}
    entityType="Tool"
    handleInputChange={handleInputChange}
    errors={errors}
  />
)}

{isEditing && isCurrentUserAdmin && !isCoreTool && (
  <OwnerSelection
    currentOwnerId={tool.ownerId}
    onOwnerChange={(newOwnerId) => handleInputChange('ownerId', newOwnerId)}
    isReadOnly={readOnly}
  />
)}

{isEditing && isCurrentUserAdmin && isCoreTool && adminServiceUserId && (
  <div className="border p-4 rounded-md bg-blue-50">
    <h3 className="text-sm font-medium text-blue-800 mb-2">Owner</h3>
    <p className="text-sm text-blue-700">
      This Core Tool will be owned by the admin service account: <span className="font-semibold">{ADMIN_SERVICE_USER}</span>
    </p>
  </div>
)}

<div className="flex justify-end gap-2 pt-4">
  {isEditing && onDiscardChanges && (
    <Button
      variant="outline"
      onClick={onDiscardChanges}
      className="border-gray-300 text-gray-700 hover:bg-gray-50"
      disabled={props.isSaving}
    >
      Cancel Edit
    </Button>
  )}
  <Button
    variant="default"
    onClick={onSubmit}
    className="bg-blue-500 hover:bg-blue-600 text-white"
    disabled={props.isSaving}
  >
    {props.isSaving ? (
      <div className="flex items-center space-x-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Saving...</span>
      </div>
    ) : (
      'Save and Test'
    )}
  </Button>
</div>
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-4">
      {isGeneratingAnnotation && <LoadingOverlay />}
      {props.isSaving && <SaveOverlay />}
      {readOnly ? renderViewMode() : renderEditMode()}
      <ConfirmationDialog
        open={parameterToDelete !== null}
        onOpenChange={(open) => !open && setParameterToDelete(null)}
        onConfirm={confirmParameterRemove}
        title="Remove Parameter"
        description="Are you sure you want to remove this parameter? This action cannot be undone."
        confirmLabel="Remove"
        cancelLabel="Cancel"
      />
    </div>
  );
});

export default ToolForm;